import { classes } from "polytype";
import {
  AggregateMetaData,
  DataModel,
  ListDataModel,
  ListQueryModel,
  ReferenceModel,
} from "../../base";
import assertObjectFound from "../../base/assertObjectFound.js";
import { language } from "../../base/i18n/setup.js";
import { config } from "../../config/config.js";
import type { AsyncResourceVariant } from "../../react";
import { provideReact } from "../../react";
import { User } from "../../user";
import { Project } from "../Project";
import type { ProjectRole } from "../ProjectMembership";
import type {
  ProjectInviteCreateRequestData,
  ProjectInviteData,
  ProjectInviteListItemData,
  ProjectInviteListQueryData,
} from "./types.js";

export class ProjectInvite extends ReferenceModel {
  public static aggregateMetaData = new AggregateMetaData(
    "membership",
    "projectinvite",
  );

  public static ofId(id: string) {
    return new ProjectInvite(id);
  }

  public static find = provideReact(async (id: string) => {
    const data = await config.behaviors.projectInvite.find(id);
    if (data) {
      return new ProjectInviteDetailed(data);
    }
  });

  public static get = provideReact(async (id: string) => {
    const projectInvite = await this.find(id);
    assertObjectFound(projectInvite, "projectInvite", id);
    return projectInvite;
  });

  public findDetailed = provideReact(
    () => ProjectInvite.find(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<ProjectInviteDetailed | undefined>>;

  public getDetailed = provideReact(
    () => ProjectInvite.get(this.id),
    [this.id],
  ) as AsyncResourceVariant<() => Promise<ProjectInviteDetailed>>;

  public static query(
    project: Project,
    query: ProjectInviteListQueryData = {},
  ) {
    return new ProjectInviteListQuery(project, query);
  }

  public static async create(
    project: Project,
    data: ProjectInviteCreateRequestData,
  ) {
    const response = await config.behaviors.projectInvite.create(
      project.id,
      data,
    );

    if ("id" in response) {
      return new ProjectInvite(response.id);
    }

    return response.map((e) => ({
      ...e,
      path: e.path === "mail_address" ? "mailAddress" : e.path,
    }));
  }

  public async delete() {
    await config.behaviors.projectInvite.delete(this.id);
  }

  public static async acceptWithToken(invitationToken: string) {
    const invite =
      await config.behaviors.projectInvite.getByToken(invitationToken);

    await config.behaviors.projectInvite.accept(invite.id, invitationToken);
  }

  public static listIncoming = provideReact(async () => {
    const data = await config.behaviors.projectInvite.listIncoming();

    return data.items.map((i) => new ProjectInviteListItem(i));
  });

  public async decline() {
    await config.behaviors.projectInvite.decline(this.id);
  }
}

class ProjectInviteCommon extends classes(
  DataModel<ProjectInviteData | ProjectInviteListItemData>,
  ProjectInvite,
) {
  public readonly role: ProjectRole;
  public readonly translatedRole: string;
  public readonly mailAddress: string;
  public readonly projectDescription: string;
  public readonly invitedBy: User;
  public readonly message?: string;
  public readonly project: Project;

  public constructor(data: ProjectInviteData | ProjectInviteListItemData) {
    super([data], [data.id]);
    this.role = data.role;
    this.translatedRole = language.default.project.role[this.role];
    this.mailAddress = data.mailAddress;
    this.projectDescription = data.projectDescription;
    this.invitedBy = User.ofId(data.information.invitedBy);
    this.message = data.message;
    this.project = Project.ofId(data.projectId);
  }

  public async accept() {
    await config.behaviors.projectInvite.accept(this.id);
  }
}

export class ProjectInviteDetailed extends classes(
  ProjectInviteCommon,
  DataModel<ProjectInviteData>,
) {
  public constructor(data: ProjectInviteData) {
    super([data], [data]);
  }
}

export class ProjectInviteListItem extends classes(
  ProjectInviteCommon,
  DataModel<ProjectInviteListItemData>,
) {
  public constructor(data: ProjectInviteListItemData) {
    super([data], [data]);
  }
}

export class ProjectInviteListQuery extends ListQueryModel<ProjectInviteListQueryData> {
  public readonly project: Project;

  public constructor(project: Project, query: ProjectInviteListQueryData = {}) {
    super(query, { dependencies: [project.id] });
    this.project = project;
  }

  public refine(query: ProjectInviteListQueryData) {
    return new ProjectInviteListQuery(this.project, {
      ...this.query,
      ...query,
    });
  }

  public execute = provideReact(async () => {
    const { items } = await config.behaviors.projectInvite.list(
      this.project.id,
      this.query,
    );

    return new ProjectInviteList(
      this.project,
      this.query,
      items.map((d) => new ProjectInviteListItem(d)),
    );
  }, [this.queryId, "projectInvite"]);
}

export class ProjectInviteList extends classes(
  ProjectInviteListQuery,
  ListDataModel<ProjectInviteListItem>,
) {
  public constructor(
    project: Project,
    query: ProjectInviteListQueryData,
    invites: ProjectInviteListItem[],
  ) {
    super([project, query], [invites, invites.length]);
  }
}
